// R-131-OVERVIEW-UX-C
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bug, Paperclip, X } from 'lucide-react';
import { toast } from 'sonner';
import type {
  BugAttachment,
  BugCategory,
  BugContext,
  BugReportSeverity,
  BugReportSubmitInput,
} from '../../types/bugReport';
import {
  BUG_ATTACHMENT_MAX_COUNT,
  BUG_ATTACHMENT_MAX_BYTES,
  BUG_DESCRIPTION_MIN_LEN,
} from '../../types/bugReport';
import { submitBugReport } from '../../lib/bugReports/bugReportService';
import { uploadBugAttachment, isAllowedAttachmentFile } from '../../services/bugAttachmentService';
import { auth } from '../../firebase';
import { logAuditEvent } from '../../lib/telemetry/auditTelemetry';
import {
  issueSeverityToAudit,
  issueSeverityToLegacy,
  type IssueSeverityDisplay,
} from '../issues/issueSeverity';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';

/** @deprecated Use BugContext from types/bugReport — kept for BetaBanner compat. */
export type ReportBugContext = BugContext;

const CATEGORIES: { value: BugCategory; label: string }[] = [
  { value: 'incorrect-fact', label: 'Incorrect fact' },
  { value: 'missing-citation', label: 'Missing citation' },
  { value: 'hallucination', label: 'Hallucination' },
  { value: 'formatting', label: 'Formatting' },
  { value: 'ui', label: 'UI / UX' },
  { value: 'other', label: 'Other' },
];

const SEVERITIES: IssueSeverityDisplay[] = ['Low', 'Medium', 'High', 'Blocking'];

const LEGACY_SEVERITIES: BugReportSeverity[] = ['CRITICAL', 'STANDARD', 'SUGGESTION'];

/**
 * R-131-OVERVIEW-UX-C / B1 — minimum description length for a non-legacy
 * report. Re-exported from the shared types module (single source of truth)
 * so the client gate (live "N more characters" hint + disabled Submit) and the
 * server router enforce the EXACT same value — no 15-vs-20 drift.
 */
const DESCRIPTION_MIN_LEN = BUG_DESCRIPTION_MIN_LEN;

/** R-131-ATTACH — accept attribute for the file picker (images + PDF). */
const ATTACHMENT_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif,application/pdf';

/** R-131-ATTACH — per-file upload lifecycle entry held in modal state. */
interface AttachmentItem {
  id: string;
  name: string;
  size: number;
  status: 'uploading' | 'done' | 'error';
  progress: number; // 0–1
  /** Set once the upload resolves; this is what gets submitted. */
  ref?: BugAttachment;
  error?: string;
}

export interface ReportBugButtonProps {
  siteId: string;
  reportId?: string;
  findingId?: string;
  context: BugContext;
  variant?: 'compact' | 'icon';
  className?: string;
}

export function ReportIssueModal({
  open,
  onClose,
  siteId,
  reportId,
  findingId,
  context,
  useLegacySeverity,
}: {
  open: boolean;
  onClose: () => void;
  siteId: string;
  reportId?: string;
  findingId?: string;
  context: BugContext;
  useLegacySeverity?: boolean;
}) {
  const [severity, setSeverity] = useState<IssueSeverityDisplay>('Medium');
  const [legacySeverity, setLegacySeverity] = useState<BugReportSeverity>('STANDARD');
  const [category, setCategory] = useState<BugCategory>('other');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // Honeypot — hidden from humans; only bots fill it. Server drops if non-empty.
  const [website, setWebsite] = useState('');
  const [attachContext, setAttachContext] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // R-131-ATTACH — per-file upload status while the modal is open.
  const [files, setFiles] = useState<AttachmentItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && !submittedId && !useLegacySeverity) {
      const timer = window.setTimeout(() => descriptionRef.current?.focus(), 0);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [open, submittedId, useLegacySeverity]);

  const errorMessage = (value: unknown): string => {
    if (value instanceof Error && value.message) return value.message;
    if (typeof value === 'string' && value.trim()) return value;
    return 'Issue submit failed. Please try again.';
  };

  const minLen = useLegacySeverity ? 1 : DESCRIPTION_MIN_LEN;
  const trimmedLen = description.trim().length;
  const charsNeeded = Math.max(0, minLen - trimmedLen);
  const meetsLength = useLegacySeverity
    ? title.trim().length > 0 && description.trim().length > 0
    : trimmedLen >= minLen;
  // R-131-ATTACH — block submit while any attachment is still uploading so we
  // never drop a half-uploaded file. Errored files don't block (the user can
  // remove them); they just won't be included.
  const canSubmit = meetsLength && !uploading;

  const buildContext = (): BugContext => {
    const base: BugContext = {
      ...context,
      siteId,
      reportId: reportId ?? context.reportId,
      findingId: findingId ?? context.findingId,
    };
    if (!attachContext) return base;
    return {
      ...base,
      url: typeof window !== 'undefined' ? window.location.href : context.url,
      currentRoute: typeof window !== 'undefined' ? window.location.pathname : context.currentRoute,
      reportId: reportId ?? context.reportId,
      findingId: findingId ?? context.findingId,
    };
  };

  // R-131-ATTACH — pick files, upload each to the per-user prefix, and track
  // per-file progress/errors. Enforces the count cap before uploading.
  const handleFilesSelected = async (selected: FileList | null) => {
    if (!selected || selected.length === 0) return;
    const uid = auth.currentUser?.uid;
    const remaining = BUG_ATTACHMENT_MAX_COUNT - files.length;
    if (remaining <= 0) {
      toast.error(`You can attach at most ${BUG_ATTACHMENT_MAX_COUNT} files.`);
      return;
    }
    const chosen = Array.from(selected).slice(0, remaining);
    if (selected.length > remaining) {
      toast.error(`Only the first ${remaining} file(s) were added (max ${BUG_ATTACHMENT_MAX_COUNT}).`);
    }
    // Clear the native input now so re-selecting the same file fires onChange.
    if (fileInputRef.current) fileInputRef.current.value = '';

    setUploading(true);
    try {
      for (const file of chosen) {
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        // Fail fast on client-side validation before adding an uploading row.
        if (!isAllowedAttachmentFile(file)) {
          setFiles((prev) => [...prev, { id, name: file.name, size: file.size, status: 'error', progress: 0, error: 'Unsupported type — use an image or PDF.' }]);
          continue;
        }
        if (file.size > BUG_ATTACHMENT_MAX_BYTES) {
          setFiles((prev) => [...prev, { id, name: file.name, size: file.size, status: 'error', progress: 0, error: 'Too large (max 10 MB).' }]);
          continue;
        }
        setFiles((prev) => [...prev, { id, name: file.name, size: file.size, status: 'uploading', progress: 0 }]);
        try {
          const ref = await uploadBugAttachment(file, uid ?? '', (p) => {
            setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, progress: p.progress } : f)));
          });
          setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, status: 'done', progress: 1, ref } : f)));
        } catch (e) {
          const message = errorMessage(e);
          setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, status: 'error', error: message } : f)));
        }
      }
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const autoTitle = title.trim() || `[${category}] ${findingId ? `Finding ${findingId.slice(0, 8)}` : reportId ? `Report ${reportId.slice(0, 8)}` : 'Site issue'}`;
      const enrichedContext = buildContext();
      const attachments = files
        .filter((f): f is AttachmentItem & { ref: BugAttachment } => f.status === 'done' && !!f.ref)
        .map((f) => f.ref);
      const payload: BugReportSubmitInput = {
        source: findingId || reportId ? 'audit' : 'user',
        title: autoTitle,
        description: description.trim(),
        category: useLegacySeverity ? undefined : category,
        auditSeverity: useLegacySeverity ? undefined : issueSeverityToAudit(severity),
        severity: useLegacySeverity ? legacySeverity : issueSeverityToLegacy(severity),
        findingId,
        context: enrichedContext,
        attachments: attachments.length > 0 ? attachments : undefined,
        website: website || undefined,
      };
      const id = await submitBugReport(payload);
      logAuditEvent({
        type: 'bug-submitted',
        siteId,
        reportId,
        reportType: context.reportType,
        payload: { bugReportId: id, findingId },
      });
      setSubmittedId(id);
      toast.success(`Issue reported — #${id.slice(-6)}`);
    } catch (e) {
      const message = errorMessage(e);
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // Reset ALL form state on close so the next open starts a fresh report. The
  // modal is kept mounted by its parents (toggled via `open`), so without this
  // the previous submission's success screen — and any typed content — would
  // persist into the next open. Every close path (✕/Escape/backdrop via
  // onOpenChange, plus the Close/Cancel/View buttons) routes through here.
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setSubmittedId(null);
      setTitle('');
      setDescription('');
      setWebsite('');
      setCategory('other');
      setSeverity('Medium');
      setLegacySeverity('STANDARD');
      setAttachContext(true);
      setError(null);
      setFiles([]);
      setUploading(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        size="md"
        className="bg-background p-6"
        data-testid="report-bug-modal"
        aria-labelledby="report-bug-title"
      >
        {submittedId ? (
          <>
            <DialogHeader>
              <DialogTitle>Issue submitted</DialogTitle>
              <DialogDescription>
                Tracked as #{submittedId.slice(-6)}. We&apos;ll notify you when status changes.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="border-0 bg-transparent p-0">
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>Close</Button>
              <Link
                to="/issues"
                className="inline-flex h-8 items-center justify-center rounded-lg bg-primary px-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/80"
                data-testid="report-bug-view-issues"
                onClick={() => handleOpenChange(false)}
              >
                View on My Issues
              </Link>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle id="report-bug-title">
                {useLegacySeverity ? 'Report a bug' : 'Report an issue'}
              </DialogTitle>
              <DialogDescription>
                Describe what happened so our team can investigate.
              </DialogDescription>
            </DialogHeader>

            {/* Honeypot — visually hidden bot trap. Real users never see or fill this. */}
            <input
              type="text"
              name="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              autoComplete="off"
              tabIndex={-1}
              aria-hidden="true"
              data-testid="report-bug-honeypot"
              className="absolute left-[-9999px] h-px w-px overflow-hidden opacity-0"
              style={{ position: 'absolute', left: '-9999px' }}
            />

            {useLegacySeverity ? (
              <fieldset>
                <legend className="mb-2 text-sm font-medium">Severity</legend>
                <div className="flex flex-wrap gap-3">
                  {LEGACY_SEVERITIES.map((s) => (
                    <label key={s} className="flex items-center gap-1 text-sm">
                      <input
                        type="radio"
                        checked={legacySeverity === s}
                        onChange={() => setLegacySeverity(s)}
                      />
                      {s}
                    </label>
                  ))}
                </div>
              </fieldset>
            ) : (
              <>
                <label className="block text-sm font-medium">
                  What happened?
                  <textarea
                    ref={descriptionRef}
                    autoFocus
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={5}
                    className="mt-1 w-full rounded border border-border bg-surface-2 px-3 py-2 text-sm"
                    data-testid="report-bug-description"
                    aria-describedby="report-bug-char-hint"
                    aria-invalid={!meetsLength}
                    placeholder={`Describe the issue (min ${minLen} characters)`}
                  />
                </label>
                {/* R-131-OVERVIEW-UX-C — dual feedback: counter turns red below
                    the minimum, and an aria-live hint tells the user exactly how
                    many more characters are needed. */}
                <p
                  id="report-bug-char-hint"
                  aria-live="polite"
                  className={`text-xs ${meetsLength ? 'text-text-muted' : 'text-danger'}`}
                  data-testid="report-bug-char-hint"
                >
                  {meetsLength
                    ? `${trimmedLen} characters`
                    : `${charsNeeded} more character${charsNeeded === 1 ? '' : 's'} needed (${trimmedLen}/${minLen})`}
                </p>

                <label className="block text-sm font-medium">
                  Category
                  <select
                    className="mt-1 w-full rounded border border-border bg-surface-2 px-3 py-2 text-sm"
                    value={category}
                    onChange={(e) => setCategory(e.target.value as BugCategory)}
                    data-testid="report-bug-category"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </label>

                <fieldset>
                  <legend className="mb-2 text-sm font-medium">Severity</legend>
                  <div className="flex flex-wrap gap-3">
                    {SEVERITIES.map((s) => (
                      <label key={s} className="flex items-center gap-1 text-sm">
                        <input
                          type="radio"
                          name="bug-severity"
                          checked={severity === s}
                          onChange={() => setSeverity(s)}
                          data-testid={`report-bug-severity-${s.toLowerCase()}`}
                        />
                        {s}
                      </label>
                    ))}
                  </div>
                </fieldset>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={attachContext}
                    onChange={(e) => setAttachContext(e.target.checked)}
                    data-testid="report-bug-attach-context"
                  />
                  Attach context (page URL, report ID, finding ID)
                </label>

                {/* R-131-ATTACH — screenshot / file attachments (separate from
                    the metadata "Attach context" checkbox above). */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Attachments</span>
                    <span className="text-xs text-text-muted">
                      {files.length}/{BUG_ATTACHMENT_MAX_COUNT} · images or PDF, up to 10 MB each
                    </span>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={ATTACHMENT_ACCEPT}
                    className="hidden"
                    data-testid="report-bug-file-input"
                    onChange={(e) => void handleFilesSelected(e.target.files)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="report-bug-attach-files"
                    disabled={files.length >= BUG_ATTACHMENT_MAX_COUNT}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Paperclip className="mr-1 h-3.5 w-3.5" aria-hidden />
                    Attach files
                  </Button>
                  {files.length > 0 && (
                    <ul className="space-y-1" data-testid="report-bug-file-list">
                      {files.map((f) => (
                        <li
                          key={f.id}
                          className="flex items-center gap-2 rounded border border-border bg-surface-2 px-2 py-1 text-xs"
                          data-testid="report-bug-file-item"
                        >
                          <span className="min-w-0 flex-1 truncate" title={f.name}>{f.name}</span>
                          {f.status === 'uploading' && (
                            <span className="text-text-muted" aria-live="polite">
                              {Math.round(f.progress * 100)}%
                            </span>
                          )}
                          {f.status === 'done' && (
                            <span className="text-success" aria-label="uploaded">✓</span>
                          )}
                          {f.status === 'error' && (
                            <span className="text-danger" title={f.error}>{f.error ?? 'Failed'}</span>
                          )}
                          <button
                            type="button"
                            aria-label={`Remove ${f.name}`}
                            data-testid="report-bug-file-remove"
                            className="shrink-0 text-text-muted hover:text-danger"
                            onClick={() => removeFile(f.id)}
                          >
                            <X className="h-3.5 w-3.5" aria-hidden />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}

            {useLegacySeverity && (
              <>
                <label className="block text-sm font-medium">
                  Title
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    maxLength={120}
                    className="mt-1 w-full rounded border border-border px-3 py-2 text-sm"
                  />
                </label>
                <label className="block text-sm font-medium">
                  Description
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={5}
                    className="mt-1 w-full rounded border border-border px-3 py-2 text-sm"
                    data-testid="report-bug-description"
                  />
                </label>
              </>
            )}

            {error && <p className="text-sm text-danger">{error}</p>}

            <DialogFooter className="flex-col items-stretch gap-1 border-0 bg-transparent p-0 sm:flex-row sm:items-center sm:justify-end">
              {/* R-131-OVERVIEW-UX-C — visible disabled reason next to Submit. */}
              {!useLegacySeverity && !meetsLength && (
                <span
                  id="report-bug-submit-hint"
                  aria-live="polite"
                  className="text-xs text-danger sm:mr-auto"
                  data-testid="report-bug-submit-hint"
                >
                  {charsNeeded} more character{charsNeeded === 1 ? '' : 's'} needed
                </span>
              )}
              {!useLegacySeverity && meetsLength && uploading && (
                <span
                  id="report-bug-submit-hint"
                  aria-live="polite"
                  className="text-xs text-text-muted sm:mr-auto"
                  data-testid="report-bug-submit-hint"
                >
                  Waiting for attachments to finish…
                </span>
              )}
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="button"
                data-testid="report-bug-submit"
                onClick={() => void handleSubmit()}
                disabled={!canSubmit || submitting}
                aria-disabled={!canSubmit || submitting}
                aria-describedby={!useLegacySeverity && (!meetsLength || uploading) ? 'report-bug-submit-hint' : undefined}
              >
                {submitting ? 'Submitting…' : 'Submit'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Legacy modal export — BetaBanner and floating button. */
export function ReportBugModal({
  onClose,
  context = {},
}: {
  onClose: () => void;
  context?: BugContext;
}) {
  const [open, setOpen] = useState(true);
  return (
    <ReportIssueModal
      open={open}
      onClose={() => {
        setOpen(false);
        onClose();
      }}
      siteId={context.siteId ?? ''}
      reportId={context.reportId}
      context={context}
      useLegacySeverity
    />
  );
}

/** Contextual R-82 button (report / audit / site card). */
export function ReportBugButton(props: ReportBugButtonProps): React.ReactElement;
export function ReportBugButton(
  props: ReportBugButtonProps,
): React.ReactElement {
  const [open, setOpen] = useState(false);

  const { siteId, reportId, findingId, context, variant = 'compact', className = '' } = props;

  return (
    <>
      <button
        type="button"
        className={`inline-flex items-center gap-1 text-text-secondary hover:text-primary text-xs ${className}`}
        data-testid="report-bug-button"
        aria-label="Report issue"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <Bug className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {variant === 'compact' && <span>Report Issue</span>}
      </button>
      <ReportIssueModal
        open={open}
        onClose={() => setOpen(false)}
        siteId={siteId}
        reportId={reportId}
        findingId={findingId}
        context={context}
      />
    </>
  );
}
